import { Button, Table } from "@mantine/core";
import {
  IconFileCheckFilled,
  IconMailCheck,
  IconMailExclamation,
  IconProgressCheck,
} from "@tabler/icons-react";
import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import { createBatchEmail, getBatchEmail } from "api/statement.service";
import CustomPageNotification from "components/layouts/CustomPageNotification";
import { DefaultTableLayout } from "pages/admin/dataEntry/TableGridDefinitions";
import React, { useEffect, useState } from "react";
import { BatchStatementAndInvoices } from "types/model.types";

const AdminEmailStatementAndInvoices: React.FC = () => {
  const [data, setData] = React.useState<BatchStatementAndInvoices>([]);
  const [busy, setBusy] = useState(false);

  // 1. Define fetchData OUTSIDE useEffect so other functions can call it
  const fetchData = React.useCallback(async () => {
    try {
      const response = await safeApiCall(getBatchEmail());
      setData(response?.data ?? []);
    } catch (error) {
      logError(error);
      console.error("Error fetching batch email data:", error);
    }
  }, []);

  // 2. useEffect handles initial load + auto-refresh
  useEffect(() => {
    let mounted = true;

    setBusy(true);

    const load = async () => {
      await fetchData();
      if (mounted) setBusy(false);
    };

    load();

    // Auto-refresh every 5 minutes
    const interval = setInterval(
      () => {
        fetchData();
      },
      5 * 60 * 1000,
    );

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [fetchData]);

  // 3. When user creates a batch, refresh immediately
  const handleCreateBatchEmail = async () => {
    setBusy(true);
    try {
      await safeApiCall(createBatchEmail());
      await fetchData(); // <-- refresh immediately after creating batch
    } catch (error) {
      logError(error);
    } finally {
      setBusy(false);
    }
  };

  if (busy)
    return (
      <CustomPageNotification
        title="Loading..."
        message="Fetching data, please wait."
      />
    );

  return (
    <>
      <div>
        <CustomPageNotification
          title="Statement and Invoice Email Status"
          message="Please ensure all statements and invoices are correct before
              emailing.&nbsp;&nbsp;Page will auto‑refresh every 5 minutes to show latest data, or you can refresh manually by reloading the page."
        />
      </div>
      <Button style={{ margin: "10px 0px" }} onClick={handleCreateBatchEmail}>
        Create Batch Email
      </Button>
      <DefaultTableLayout>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Debtor Id</Table.Th>
            <Table.Th>Statement Created</Table.Th>
            <Table.Th>Invoice Created</Table.Th>
            <Table.Th>Email Sent</Table.Th>
          </Table.Tr>
        </Table.Thead>
        {/* Render your form table content here */}
        <Table.Tbody>
          {data.map((item, index) => (
            <Table.Tr key={`${item.id}-${index}`}>
              <Table.Td>{item.debtorId}</Table.Td>
              <Table.Td>
                {item.statementCreated ? (
                  <IconFileCheckFilled size={24} color="green" />
                ) : (
                  <IconProgressCheck size={24} />
                )}
              </Table.Td>
              <Table.Td>
                {item.invoiceCreated ? (
                  <IconFileCheckFilled size={24} color="green" />
                ) : (
                  <IconProgressCheck size={24} />
                )}
              </Table.Td>
              <Table.Td>
                {item.emailSent ? (
                  <IconMailCheck size={24} color="green" />
                ) : (
                  <IconMailExclamation size={24} />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </DefaultTableLayout>
    </>
  );
};

export default AdminEmailStatementAndInvoices;
